import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Receipt, ChevronRight, Copy, Clock, Wallet } from "lucide-react";
import * as ella from "@/lib/ellaApi";
import { useSeo } from "@/lib/seo";
import { fmtIDR, productImg, paymentImg, LOGO } from "@/lib/ellaApi";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

// Order status codes upstream: 1 unpaid (own endpoint), 2 Diproses, 3 Dikirim,
// 4 Selesai, 5 Batal.
const TABS = [
  { value: "unpaid", label: "Bayar" },
  { value: "2", label: "Diproses" },
  { value: "3", label: "Dikirim" },
  { value: "4", label: "Selesai" },
  { value: "5", label: "Batal" },
];

const STATUS_BADGE = {
  unpaid: { label: "Belum Bayar", color: "bg-amber-50 text-amber-700" },
  "2": { label: "Diproses", color: "bg-blue-50 text-blue-700" },
  "3": { label: "Dikirim", color: "bg-indigo-50 text-indigo-700" },
  "4": { label: "Selesai", color: "bg-emerald-50 text-emerald-700" },
  "5": { label: "Dibatalkan", color: "bg-red-50 text-red-700" },
};

const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Disalin!"); };

/** Unpaid orders card — payment instruction summary (legacy transaction-unpaid). */
function UnpaidCard({ t }) {
  const methodLabel = (t.PaymentMethod || "").split("|")[1] || t.PaymentMethod || "-";
  const isGopay = t.PaymentMethodCategory === "gopay";
  const refLabel = t.PaymentMethodCategory === "bank_transfer" ? "No. Virtual Account"
    : t.PaymentMethodCategory === "cstore" ? "Payment Code"
    : t.PaymentMethodCategory === "echannel" ? "Bill Key"
    : "Referensi";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-3" data-testid={`transaction-${t.ID}`}>
      <div className="flex items-center justify-between gap-2 pb-2 border-b border-gray-100">
        <Link to={`/account/transactions/${encodeURIComponent(t.ID)}`} className="flex items-center gap-1.5 min-w-0">
          <Receipt className="w-3.5 h-3.5 text-[#D32F2F] shrink-0" />
          <span className="text-[11px] text-gray-500 truncate font-mono">{t.ID}</span>
        </Link>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 bg-amber-50 text-amber-700">Belum Bayar</span>
      </div>

      <div className="flex items-center gap-3 mt-2">
        <img
          src={t.PaymentLogo ? paymentImg(t.PaymentLogo) : LOGO}
          alt={methodLabel}
          className="w-12 h-8 object-contain bg-gray-50 rounded-lg p-1 shrink-0"
          onError={(e) => { e.target.src = LOGO; e.target.classList.add("opacity-40"); }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{methodLabel}</p>
          <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" /> Bayar sebelum {t.ExpiredDate}
          </p>
        </div>
        <span className="text-sm font-bold text-[#D32F2F] shrink-0" data-testid={`unpaid-amount-${t.ID}`}>{fmtIDR(t.GrossAmount)}</span>
      </div>

      {!isGopay && t.ReferenceID && (
        <div className="mt-2 p-2.5 bg-gray-50 rounded-xl flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] text-gray-500">{refLabel}</p>
            <p className="font-mono font-bold text-sm break-all" data-testid={`unpaid-reference-${t.ID}`}>{t.ReferenceID}</p>
          </div>
          <button onClick={() => copy(t.ReferenceID)} className="text-[#D32F2F] shrink-0 p-1" data-testid={`unpaid-copy-${t.ID}`}>
            <Copy className="w-4 h-4" />
          </button>
        </div>
      )}

      {isGopay && t.GopayDeepLink && (
        <button
          onClick={() => window.open(t.GopayDeepLink, "_blank")}
          className="mt-2 w-full h-10 rounded-full bg-[#00AA13] hover:bg-[#008d10] text-white text-sm font-semibold flex items-center justify-center gap-2"
          data-testid={`unpaid-gopay-${t.ID}`}
        >
          <Wallet className="w-4 h-4" /> Bayar dengan GoPay
        </button>
      )}
    </div>
  );
}

function TransactionList({ tab }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const req = tab === "unpaid" ? ella.getUnpaidTransactions() : ella.getTransactions(tab);
    req.then((res) => setItems(res.status ? res.data || [] : [])).finally(() => setLoading(false));
  }, [tab]);

  if (loading) {
    return <div className="p-4 space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 skeleton rounded-2xl" />)}</div>;
  }
  if (!items.length) {
    return <div className="p-8 text-center"><Receipt className="w-12 h-12 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-500">Belum ada transaksi</p></div>;
  }

  if (tab === "unpaid") {
    return (
      <div className="p-4 space-y-2">
        {items.map((t) => <UnpaidCard key={t.ID} t={t} />)}
      </div>
    );
  }

  const badge = STATUS_BADGE[tab];
  return (
    <div className="p-4 space-y-2">
      {items.map((t) => {
        const id = t.ID;
        const totalItem = Number(t.TotalItem || 1);
        const extra = totalItem > 1 ? totalItem - 1 : 0;
        return (
          <Link
            key={id}
            to={`/account/transactions/${encodeURIComponent(id)}`}
            className="bg-white rounded-2xl border border-gray-100 p-3 hover:shadow-sm block"
            data-testid={`transaction-${id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-gray-100">
              <div className="flex items-center gap-1.5 min-w-0">
                <Receipt className="w-3.5 h-3.5 text-[#D32F2F] shrink-0" />
                <span className="text-[11px] text-gray-500 truncate font-mono">{t.ID}</span>
              </div>
              {badge && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${badge.color}`}>{badge.label}</span>}
            </div>
            <div className="flex items-center gap-3">
              <img
                src={productImg(t.ImagePath)}
                alt={t.Product}
                className="w-14 h-14 rounded-xl object-cover bg-gray-50 shrink-0"
                onError={(e) => { e.target.src = LOGO; e.target.classList.add("object-contain", "p-2", "opacity-40"); }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 line-clamp-1" data-testid={`transaction-product-${id}`}>{t.Product || "-"}</p>
                {extra > 0 && <p className="text-[11px] text-gray-500 mt-0.5">+{extra} produk lainnya</p>}
                <p className="text-[11px] text-gray-500 mt-0.5">Cabang {t.Branch} • {t.CreatedDate?.split(" ")[0]}</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
              <span className="text-[11px] text-gray-500">Total Belanja</span>
              <div className="flex items-center gap-1">
                <span className="text-sm font-bold text-[#D32F2F]">{fmtIDR(t.GrossAmount || 0)}</span>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export default function TransactionsPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("unpaid");

  useSeo({ title: "Transaksi" });
  return (
    <div className="pb-24">
      <Tabs value={tab} onValueChange={setTab}>
        <div style={{ top: "var(--app-header-height, 60px)" }} className="sticky bg-white z-30">
      <div className="px-4 py-3 flex items-center gap-3 bg-white border-b border-gray-100">
        <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display text-lg font-bold text-gray-900">Transaksi</h1>
      </div>
        <div className="bg-white border-b border-gray-100 overflow-x-auto no-scrollbar">
          <TabsList className="w-max justify-start bg-transparent rounded-none h-auto p-0 gap-0">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="text-xs px-4 py-3 border-b-2 border-transparent data-[state=active]:border-[#D32F2F] data-[state=active]:text-[#D32F2F] data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none whitespace-nowrap"
                data-testid={`tab-${t.value}`}
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        </div>
        {TABS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-0">
            <TransactionList tab={t.value} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
